import { cloneElement } from "react";
import type { ReactElement, ReactNode } from "react";

/**
 * Clone a pre-built icon element with a new size (and optional stroke/color).
 * Mirrors the prototype's `React.cloneElement(Icon, { size, stroke })` pattern.
 */
export function ic(
  el: ReactElement,
  size: number,
  extra?: { stroke?: number; sw?: number; color?: string; fill?: string },
): ReactElement {
  return cloneElement(el, { size, ...extra } as Record<string, unknown>);
}

/**
 * Inline SVG icon set for the Quick Setup wizard, ported verbatim from the
 * Claude Design prototype (edit-product-shared.jsx + quick-setup-data.jsx).
 * Kept self-contained so the wizard never depends on portal-wide icon choices.
 */

type IconProps = {
  d: string | ReactNode;
  size?: number;
  stroke?: number;
  fill?: string;
  color?: string;
};

export function Icon({
  d,
  size = 16,
  stroke = 1.6,
  fill = "none",
  color = "currentColor",
}: IconProps): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke={color}
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {typeof d === "string" ? <path d={d} /> : d}
    </svg>
  );
}

export const Icons = {
  back: <Icon d="M15 18l-6-6 6-6" />,
  globe: (
    <Icon
      d={
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18" />
          <path d="M12 3c2.5 2.4 3.9 5.6 3.9 9s-1.4 6.6-3.9 9c-2.5-2.4-3.9-5.6-3.9-9s1.4-6.6 3.9-9z" />
        </>
      }
    />
  ),
  search: (
    <Icon
      d={
        <>
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </>
      }
    />
  ),
  plus: <Icon d="M12 5v14M5 12h14" />,
  chevronRight: <Icon d="M9 6l6 6-6 6" />,
  trash: (
    <Icon
      d={
        <>
          <path d="M3 6h18" />
          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        </>
      }
    />
  ),
  camera: (
    <Icon
      d={
        <>
          <path d="M3 7h3l2-3h8l2 3h3v13H3z" />
          <circle cx="12" cy="13" r="4" />
        </>
      }
    />
  ),
  check: <Icon d="M20 6L9 17l-5-5" />,
  sparkle: <Icon d="M12 2l1.8 5.5L19 9l-5.2 1.5L12 16l-1.8-5.5L5 9l5.2-1.5L12 2z" />,
  edit: (
    <Icon
      d={
        <>
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </>
      }
    />
  ),
} as const;

type QSIconProps = {
  d: ReactNode;
  size?: number;
  sw?: number;
};

export function QSIcon({ d, size = 18, sw = 1.7 }: QSIconProps): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {d}
    </svg>
  );
}

export const QS_GLYPH = {
  shirt: <QSIcon d={<path d="M16 3l4 2-2 4-2-1v13H8V8L6 9 4 5l4-2 2 2h4l2-2z" />} />,
  chip: (
    <QSIcon
      d={
        <>
          <rect x="6" y="6" width="12" height="12" rx="2" />
          <path d="M9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3" />
        </>
      }
    />
  ),
  cup: (
    <QSIcon
      d={
        <>
          <path d="M4 8h13v5a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5V8z" />
          <path d="M17 9h2.5a2.5 2.5 0 0 1 0 5H17" />
          <path d="M7 2v2M11 2v2" />
        </>
      }
    />
  ),
  sparkles: (
    <QSIcon
      d={
        <>
          <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6L12 3z" />
          <path d="M19 14l.7 1.9L21 17l-1.3.6L19 19l-.6-1.4L17 17l1.4-.6L19 14z" />
        </>
      }
    />
  ),
  droplet: (
    <QSIcon d={<path d="M12 3s6 5.5 6 10a6 6 0 0 1-12 0c0-4.5 6-10 6-10z" />} />
  ),
  gem: (
    <QSIcon
      d={
        <>
          <path d="M6 3h12l4 6-10 12L2 9l4-6z" />
          <path d="M2 9h20" />
          <path d="M11 3 8 9l4 12 4-12-3-6" />
        </>
      }
    />
  ),
  sofa: (
    <QSIcon
      d={
        <>
          <path d="M4 11V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3" />
          <path d="M2 13a2 2 0 0 1 4 0v3h12v-3a2 2 0 0 1 4 0v5H2v-5z" />
        </>
      }
    />
  ),
  hand: (
    <QSIcon
      d={
        <path d="M8 13V5a1.5 1.5 0 0 1 3 0v6M11 11V4a1.5 1.5 0 0 1 3 0v7M14 11V6a1.5 1.5 0 0 1 3 0v9a6 6 0 0 1-6 6h-1a6 6 0 0 1-5-3l-2.5-4a1.5 1.5 0 0 1 2.5-1.6L8 14" />
      }
    />
  ),
  grid: (
    <QSIcon
      d={
        <>
          <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
          <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
          <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
          <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
        </>
      }
    />
  ),
  wallet: (
    <QSIcon
      d={
        <>
          <path d="M3 7a2 2 0 0 1 2-2h12v3" />
          <rect x="3" y="6" width="18" height="14" rx="2" />
          <path d="M16 13h3" />
        </>
      }
    />
  ),
  cash: (
    <QSIcon
      d={
        <>
          <rect x="2" y="6" width="20" height="12" rx="2" />
          <circle cx="12" cy="12" r="2.5" />
          <path d="M6 9v6M18 9v6" />
        </>
      }
    />
  ),
  bank: (
    <QSIcon
      d={
        <>
          <path d="M3 10l9-6 9 6" />
          <path d="M5 10v8M10 10v8M14 10v8M19 10v8M3 21h18" />
        </>
      }
    />
  ),
  mobile: (
    <QSIcon
      d={
        <>
          <rect x="7" y="2" width="10" height="20" rx="2" />
          <path d="M11 18h2" />
        </>
      }
    />
  ),
  pin: (
    <QSIcon
      d={
        <>
          <path d="M12 21s-7-6.3-7-11a7 7 0 0 1 14 0c0 4.7-7 11-7 11z" />
          <circle cx="12" cy="10" r="2.5" />
        </>
      }
    />
  ),
  whats: (
    <QSIcon
      d={
        <>
          <path d="M21 11.5a8.4 8.4 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.9-.9L3 21l1.9-5.6a8.4 8.4 0 0 1-1-4A8.5 8.5 0 0 1 12.5 3 8.4 8.4 0 0 1 21 11.5z" />
          <path d="M9 9.5c0 3 2.5 5.5 5.5 5.5l1-1.4-1.8-.9-1 .8c-.8-.4-1.6-1.2-2-2l.8-1L10 8.6 9 9.5z" />
        </>
      }
    />
  ),
  wand: (
    <QSIcon
      d={<path d="M15 4V2M15 10V8M12.5 5.5h-2M19.5 5.5h-2M4 20l9-9M14.5 6.5l3 3" />}
    />
  ),
  tag: (
    <QSIcon
      d={
        <>
          <path d="M3 7v4.6a2 2 0 0 0 .6 1.4l7.4 7.4a2 2 0 0 0 2.8 0l4.8-4.8a2 2 0 0 0 0-2.8L11.2 5.4A2 2 0 0 0 9.8 4.8L5 4.8" />
          <circle cx="7" cy="8" r="1.4" />
        </>
      }
    />
  ),
  image: (
    <QSIcon
      d={
        <>
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <circle cx="8.5" cy="9.5" r="1.6" />
          <path d="M21 16l-5-5L4 20" />
        </>
      }
    />
  ),
} as const;

export type GlyphKey = keyof typeof QS_GLYPH;
